import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getSafeSignals, createSafeSignal, getSafeChecks, createSafeCheck, extractErrorMessage } from '../services/api';
import { FaPlus, FaShieldAlt, FaCircle, FaClipboardCheck } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';

function SafetySystem({ user }) {
  const toast = useToast();
  const [signals, setSignals] = useState([]);
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('status');
  const [showSignalModal, setShowSignalModal] = useState(false);
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [savingSignal, setSavingSignal] = useState(false);
  const [savingCheck, setSavingCheck] = useState(false);
  const [formErrorSignal, setFormErrorSignal] = useState(null);
  const [formErrorCheck, setFormErrorCheck] = useState(null);
  const [newSignal, setNewSignal] = useState({
    user_id: '',
    level: 'green',
    reason: '',
    context: ''
  });
  const [newCheck, setNewCheck] = useState({
    user_id: '',
    notes: '',
    response: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [signalsData, checksData] = await Promise.all([
        getSafeSignals(),
        getSafeChecks().catch(() => [])
      ]);
      setSignals(signalsData.signals || signalsData || []);
      setChecks(checksData.checks || checksData || []);
    } catch (error) {
      console.error('Error fetching safety data:', error);
      toast.error(extractErrorMessage(error, 'Sicherheitsdaten konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSignal = async (e) => {
    e.preventDefault();
    if (savingSignal) return;
    setSavingSignal(true);
    setFormErrorSignal(null);
    try {
      await createSafeSignal(newSignal);
      setNewSignal({ user_id: '', level: 'green', reason: '', context: '' });
      setShowSignalModal(false);
      toast.success('Signal gesendet');
      fetchData();
    } catch (error) {
      console.error('Error creating signal:', error);
      setFormErrorSignal(extractErrorMessage(error, 'Fehler beim Erstellen des Signals'));
    } finally {
      setSavingSignal(false);
    }
  };

  const handleCreateCheck = async (e) => {
    e.preventDefault();
    if (savingCheck) return;
    setSavingCheck(true);
    setFormErrorCheck(null);
    try {
      await createSafeCheck(newCheck);
      setNewCheck({ user_id: '', notes: '', response: '' });
      setShowCheckModal(false);
      toast.success('Safe-Check erstellt');
      fetchData();
    } catch (error) {
      console.error('Error creating check:', error);
      setFormErrorCheck(extractErrorMessage(error, 'Fehler beim Erstellen des Safe-Checks'));
    } finally {
      setSavingCheck(false);
    }
  };

  const closeSignalModal = () => {
    if (savingSignal) return;
    setShowSignalModal(false);
    setFormErrorSignal(null);
  };

  const closeCheckModal = () => {
    if (savingCheck) return;
    setShowCheckModal(false);
    setFormErrorCheck(null);
  };

  const getLevelColor = (level) => {
    if (level === 'green') return '#43b581';
    if (level === 'yellow') return '#faa61a';
    if (level === 'red') return '#f04747';
    return '#72767d';
  };

  const getLevelLabel = (level) => {
    if (level === 'green') return 'Grün - Alles gut';
    if (level === 'yellow') return 'Gelb - Vorsicht';
    if (level === 'red') return 'Rot - Stop';
    return level;
  };

  const currentSignal = signals.length > 0 ? signals[0] : null;

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1><FaShieldAlt /> Sicherheitssystem</h1>
          <div className="header-actions">
            <button className="add-btn" onClick={() => setShowSignalModal(true)}>
              <FaPlus /> Neues Signal
            </button>
            <button className="add-btn secondary" onClick={() => setShowCheckModal(true)}>
              <FaPlus /> Safe-Check
            </button>
          </div>
        </div>

        <div className="tab-bar">
          <button className={`tab-btn ${activeTab === 'status' ? 'active' : ''}`} onClick={() => setActiveTab('status')}>
            <FaCircle /> Aktueller Status
          </button>
          <button className={`tab-btn ${activeTab === 'signals' ? 'active' : ''}`} onClick={() => setActiveTab('signals')}>
            <FaShieldAlt /> Signalverlauf
          </button>
          <button className={`tab-btn ${activeTab === 'checks' ? 'active' : ''}`} onClick={() => setActiveTab('checks')}>
            <FaClipboardCheck /> Safe-Checks
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : activeTab === 'status' ? (
          <div className="status-display" style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              background: '#2f3136',
              borderRadius: '16px',
              padding: '40px 60px'
            }}>
              {/* Traffic light */}
              <div style={{
                background: '#202225',
                borderRadius: '40px',
                padding: '20px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {['red', 'yellow', 'green'].map(level => (
                  <div key={level} style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: currentSignal && currentSignal.level === level
                      ? getLevelColor(level)
                      : '#36393f',
                    boxShadow: currentSignal && currentSignal.level === level
                      ? `0 0 30px ${getLevelColor(level)}80`
                      : 'none',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {currentSignal && currentSignal.level === level && (
                      <FaCircle style={{ fontSize: '30px', color: '#fff' }} />
                    )}
                  </div>
                ))}
              </div>
              <h2 style={{ color: currentSignal ? getLevelColor(currentSignal.level) : '#72767d', margin: '10px 0' }}>
                {currentSignal ? getLevelLabel(currentSignal.level) : 'Kein Signal'}
              </h2>
              {currentSignal && currentSignal.reason && (
                <p style={{ color: '#dcddde' }}>{currentSignal.reason}</p>
              )}
              {currentSignal && currentSignal.context && (
                <p style={{ color: '#8e9297', fontSize: '0.9rem' }}>{currentSignal.context}</p>
              )}
              {currentSignal && currentSignal.created_at && (
                <p style={{ color: '#72767d', fontSize: '0.85rem' }}>
                  {new Date(currentSignal.created_at).toLocaleString('de-DE')}
                </p>
              )}
            </div>
          </div>
        ) : activeTab === 'signals' ? (
          <div className="data-table-container">
            {signals.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Benutzer</th>
                    <th>Level</th>
                    <th>Grund</th>
                    <th>Kontext</th>
                  </tr>
                </thead>
                <tbody>
                  {signals.map(signal => (
                    <tr key={signal.id}>
                      <td>{new Date(signal.created_at || signal.timestamp).toLocaleString('de-DE')}</td>
                      <td>{signal.username || signal.user_id}</td>
                      <td>
                        <span className="badge" style={{ backgroundColor: getLevelColor(signal.level) }}>
                          {getLevelLabel(signal.level)}
                        </span>
                      </td>
                      <td>{signal.reason || '-'}</td>
                      <td>{signal.context || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">Keine Signale vorhanden</div>
            )}
          </div>
        ) : (
          <div className="data-table-container">
            {checks.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Benutzer</th>
                    <th>Notizen</th>
                    <th>Antwort</th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map(check => (
                    <tr key={check.id}>
                      <td>{new Date(check.created_at || check.timestamp).toLocaleString('de-DE')}</td>
                      <td>{check.username || check.user_id}</td>
                      <td>{check.notes || '-'}</td>
                      <td>{check.response || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">Keine Safe-Checks vorhanden</div>
            )}
          </div>
        )}

        {showSignalModal && (
          <div className="modal-overlay" onClick={closeSignalModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neues Sicherheitssignal</h2>
              {formErrorSignal && <div className="shared-form-error-banner">{formErrorSignal}</div>}
              <form onSubmit={handleCreateSignal}>
                <div className="form-group">
                  <UserSelect
                    value={newSignal.user_id}
                    onChange={id => setNewSignal({ ...newSignal, user_id: id })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Level *</label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    {['green', 'yellow', 'red'].map(level => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => setNewSignal({ ...newSignal, level })}
                        style={{
                          flex: 1,
                          padding: '12px',
                          borderRadius: '8px',
                          border: newSignal.level === level ? `2px solid ${getLevelColor(level)}` : '2px solid #40444b',
                          background: newSignal.level === level ? `${getLevelColor(level)}20` : '#2f3136',
                          color: getLevelColor(level),
                          cursor: 'pointer',
                          fontWeight: newSignal.level === level ? 'bold' : 'normal',
                          fontSize: '0.9rem'
                        }}
                      >
                        <FaCircle style={{ marginRight: '6px' }} />
                        {level === 'green' ? 'Grün' : level === 'yellow' ? 'Gelb' : 'Rot'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Grund</label>
                  <textarea
                    value={newSignal.reason}
                    onChange={e => setNewSignal({ ...newSignal, reason: e.target.value })}
                    placeholder="Grund für das Signal..."
                  />
                </div>
                <div className="form-group">
                  <label>Kontext</label>
                  <textarea
                    value={newSignal.context}
                    onChange={e => setNewSignal({ ...newSignal, context: e.target.value })}
                    placeholder="Zusätzlicher Kontext..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeSignalModal} disabled={savingSignal}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingSignal}>
                    {savingSignal ? 'Speichert\u2026' : 'Signal senden'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showCheckModal && (
          <div className="modal-overlay" onClick={closeCheckModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neuer Safe-Check</h2>
              {formErrorCheck && <div className="shared-form-error-banner">{formErrorCheck}</div>}
              <form onSubmit={handleCreateCheck}>
                <div className="form-group">
                  <UserSelect
                    value={newCheck.user_id}
                    onChange={id => setNewCheck({ ...newCheck, user_id: id })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={newCheck.notes}
                    onChange={e => setNewCheck({ ...newCheck, notes: e.target.value })}
                    placeholder="Notizen zum Check..."
                  />
                </div>
                <div className="form-group">
                  <label>Antwort</label>
                  <textarea
                    value={newCheck.response}
                    onChange={e => setNewCheck({ ...newCheck, response: e.target.value })}
                    placeholder="Antwort..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeCheckModal} disabled={savingCheck}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingCheck}>
                    {savingCheck ? 'Speichert\u2026' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default SafetySystem;
